import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Download, Upload, DatabaseBackup, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BackupManager = () => {
    const { token } = useAuth();
    const [info, setInfo] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const fileRef = useRef(null);
    const auth = { Authorization: `Bearer ${token || localStorage.getItem('token')}` };

    useEffect(() => {
        axios.get(`${API}/admin/backup/info`, { headers: auth }).then(r => setInfo(r.data)).catch(() => {});
        // eslint-disable-next-line
    }, []);

    const download = async () => {
        setDownloading(true);
        try {
            const res = await axios.get(`${API}/admin/backup/download`, { headers: auth, responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `vkusatnaturcia_backup_${new Date().toISOString().slice(0, 10)}.json.gz`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Резервното копие е свалено — пазете го на сигурно място!');
        } catch (e) {
            toast.error('Грешка при сваляне на бекъпа');
        } finally { setDownloading(false); }
    };

    const restore = async (file) => {
        if (!file) return;
        if (!window.confirm('⚠️ ВНИМАНИЕ: Възстановяването ЗАМЕНЯ текущите данни (продукти, категории, поръчки, промоции) с тези от файла. Продължавате ли?')) return;
        setRestoring(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${API}/admin/backup/restore?confirm=true`, fd, { headers: auth });
            toast.success(`Възстановено успешно от бекъп (${res.data.backup_date?.slice(0, 10)})`);
            const r = await axios.get(`${API}/admin/backup/info`, { headers: auth });
            setInfo(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Грешка при възстановяване');
        } finally { setRestoring(false); if (fileRef.current) fileRef.current.value = ''; }
    };

    const labels = { products: 'Продукти', categories: 'Категории', orders: 'Поръчки', brands: 'Марки', promotions: 'Промоции', carts: 'Колички', invoices: 'Фактури', email_settings: 'Имейл настройки', product_order: 'Подредби', promo_banners: 'Промо банери', users: 'Потребители' };

    return (
        <div className="p-4 sm:p-6" data-testid="backup-manager">
            <h2 className="text-2xl font-bold flex items-center gap-2 mb-1"><DatabaseBackup className="w-6 h-6 text-amber-600" /> Резервни копия</h2>
            <p className="text-sm text-muted-foreground mb-6">Сваляйте редовно копие на данните и го пазете на компютъра си — това е защитата ви срещу загуба на данни.</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {info && Object.entries(info.collections).filter(([, v]) => v > 0).map(([k, v]) => (
                    <div key={k} className="border rounded-lg p-3 text-center bg-white dark:bg-slate-800">
                        <p className="text-xl font-bold text-amber-600">{v}</p>
                        <p className="text-xs text-muted-foreground">{labels[k] || k}</p>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-3">
                <Button onClick={download} disabled={downloading} data-testid="download-backup-btn" className="gap-2">
                    <Download className="w-4 h-4" /> {downloading ? 'Подготвя се...' : 'Свали резервно копие'}
                </Button>
                <Button variant="outline" disabled={restoring} onClick={() => fileRef.current?.click()} data-testid="restore-backup-btn" className="gap-2">
                    <Upload className="w-4 h-4" /> {restoring ? 'Възстановява се...' : 'Възстанови от файл'}
                </Button>
                <input ref={fileRef} type="file" accept=".gz,.json" className="hidden" onChange={e => restore(e.target.files?.[0])} />
            </div>

            <div className="mt-6 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-800 dark:text-amber-300 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                    Бекъпът включва продукти, категории, поръчки, марки, промоции и настройки. Снимките на продуктите (над 1 GB) не се включват — те остават в базата. Препоръка: сваляйте копие поне веднъж седмично и след големи промени.
                </div>
            </div>
        </div>
    );
};

export default BackupManager;
